<template>
    <!-- Hospitality variant: one optional inline Batch No field + a "Print label" button.
         The button emits a single text-only `printTag` job onto the global $eventHub; the
         hidden tagPrint pipeline (mounted once on the host screen) rasterizes and prints it
         direct-to-PDF — no preview, no confirm/dialog chain (Story 6.2 AC-1/AC-2). -->
    <div v-if="variant == 'hospitality'" class="label-print-trigger">
        <label :for="inputId" class="sr-only">Batch number (optional)</label>
        <b-form-input
            :id="inputId"
            v-model="batchNo"
            type="text"
            size="sm"
            class="label-print-trigger__batch"
            placeholder="Batch No (optional)"
            aria-label="Batch number (optional)"
            autocomplete="off"
        />
        <b-button
            variant="outline-secondary"
            size="sm"
            class="label-print-trigger__btn"
            :disabled="printing || !canPrint"
            @click="print"
        >
            <i class="fas fa-tag mr-1" aria-hidden="true"></i> Print label
        </b-button>
    </div>
    <!-- Bulk variant (Story 6.3): a single presentational button. It owns NO order
         data — clicking emits a `bulk-print` component event and the parent
         (ordersDetails.vue) does the print-time server re-eval + $eventHub emit. -->
    <div v-else-if="variant == 'bulk'" class="label-print-trigger">
        <b-button
            variant="outline-secondary"
            size="sm"
            class="label-print-trigger__btn"
            :disabled="printing"
            @click="printBulk"
        >
            <i class="fas fa-tags mr-1" aria-hidden="true"></i> Print bulk label
        </b-button>
    </div>
</template>

<script>
export default {
    name: "LabelPrintTrigger",
    props: {
        //Open for the future "bulk" variant (Story 6.3) but only "hospitality" is
        //implemented here. Unknown variants render nothing.
        variant: {
            type: String,
            default: "hospitality",
            validator: (v) => ["hospitality", "bulk"].includes(v),
        },
        //Required for the hospitality variant (its canPrint/print() guard emptiness);
        //the bulk variant supplies no product name, so this is optional and safe.
        productName: {
            type: String,
            required: false,
            default: "",
        },
        //Accepted for the future bulk variant; unused by the hospitality variant.
        clientName: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            //Batch No is ad-hoc and lives ONLY here for the duration of the print
            //(Story 6.2 AC-3) — never written to the product, order, or any store/DB.
            batchNo: "",
            //In-flight guard: a single click disables the button for a short cooldown so
            //a rapid double-click cannot emit a second job into the shared $eventHub
            //(the tagPrint pipeline is re-entrancy-sensitive — keep one job per click).
            printing: false,
        };
    },
    computed: {
        //Unique id so the sr-only <label> binds to this instance's input even when
        //many triggers render in the same product grid.
        inputId() {
            return "label-print-batch-" + this._uid;
        },
        //Don't allow a label with no usable name to be emitted.
        canPrint() {
            return !!(this.productName && this.productName.trim().length);
        },
    },
    methods: {
        print() {
            //Guard empty/whitespace name and re-entrant clicks before emitting.
            if (this.printing || !this.canPrint) {
                return;
            }
            this.printing = true;
            const job = { label_type: "hospitality", product_name: this.productName };
            if (this.batchNo && this.batchNo.trim().length) {
                job.batch_no = this.batchNo.trim();
            }
            //Payload is an ARRAY of jobs — tagPrint.printTag() iterates the array.
            //Do NOT truncate here; tagPrint already truncates (product_name 40 / batch_no 24).
            this.$eventHub.$emit("printTag", [job]);
            //Clear the field — ad-hoc, not persisted (AC-3).
            this.batchNo = "";
            //Release the guard after a short cooldown (covers the pipeline's ~350ms
            //render window) so the button is usable again for the next print.
            setTimeout(() => {
                this.printing = false;
            }, 800);
        },
        printBulk() {
            //Re-entrancy guard mirrors print(): a rapid double-click must not fire two
            //server re-evals + two $eventHub emits. The parent does the actual work.
            if (this.printing) {
                return;
            }
            this.printing = true;
            this.$emit("bulk-print");
            setTimeout(() => {
                this.printing = false;
            }, 800);
        },
    },
};
</script>

<style lang="scss" scoped>
.label-print-trigger {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-top: var(--space-2);
}

.label-print-trigger__batch {
    max-width: 160px;
    border-radius: var(--radius-control);
}

.label-print-trigger__btn {
    white-space: nowrap;
    border-radius: var(--radius-control);
}

.label-print-trigger__batch:focus-visible,
.label-print-trigger__btn:focus-visible {
    outline: var(--focus-ring);
    outline-offset: var(--focus-ring-offset);
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
